import { useAuthStore, isTokenExpired } from '../stores/authStore'

export const API_BASE = import.meta.env.VITE_API_BASE || 'https://api.energysecurity.com'

export async function refreshAccessToken(): Promise<string | null> {
  const { refreshToken, updateAccessToken, clearAuth } = useAuthStore.getState()
  if (!refreshToken) return null

  try {
    const res = await fetch(`${API_BASE}/api/v1/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    })
    if (!res.ok) {
      clearAuth()
      return null
    }
    const data = await res.json()
    updateAccessToken(data.access_token)
    return data.access_token
  } catch {
    clearAuth()
    return null
  }
}

export async function getValidAccessToken(): Promise<string | null> {
  const { accessToken } = useAuthStore.getState()
  if (!accessToken) return null
  if (!isTokenExpired(accessToken)) return accessToken
  return refreshAccessToken()
}

export async function authFetch(url: string, options: RequestInit = {}): Promise<Response> {
  let token = await getValidAccessToken()
  if (!token) {
    useAuthStore.getState().clearAuth()
    throw new Error('Not authenticated')
  }

  const headers = new Headers(options.headers)
  headers.set('Authorization', `Bearer ${token}`)
  if (!headers.has('Content-Type') && options.body) {
    headers.set('Content-Type', 'application/json')
  }

  let res = await fetch(url, { ...options, headers })

  if (res.status === 401) {
    token = await refreshAccessToken()
    if (!token) {
      useAuthStore.getState().clearAuth()
      throw new Error('Session expired')
    }
    headers.set('Authorization', `Bearer ${token}`)
    res = await fetch(url, { ...options, headers })
  }

  return res
}

export async function apiGet<T>(path: string): Promise<T> {
  const res = await authFetch(`${API_BASE}${path}`)
  if (!res.ok) throw new Error(`API error: ${res.status}`)
  return res.json()
}

export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
  const res = await authFetch(`${API_BASE}${path}`, {
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  })
  if (!res.ok) throw new Error(`API error: ${res.status}`)
  return res.json()
}

export async function apiPut<T>(path: string, body?: unknown): Promise<T> {
  const res = await authFetch(`${API_BASE}${path}`, {
    method: 'PUT',
    body: body ? JSON.stringify(body) : undefined,
  })
  if (!res.ok) throw new Error(`API error: ${res.status}`)
  return res.json()
}

export async function apiDelete(path: string, body?: unknown): Promise<void> {
  const opts: RequestInit = { method: 'DELETE' }
  if (body) {
    opts.headers = { 'Content-Type': 'application/json' }
    opts.body = JSON.stringify(body)
  }
  const res = await authFetch(`${API_BASE}${path}`, opts)
  if (!res.ok) throw new Error(`API error: ${res.status}`)
}

export async function login(email: string, password: string) {
  const res = await fetch(`${API_BASE}/api/v1/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.error || 'Login failed')
  }
  return res.json()
}

export async function logout() {
  try {
    await authFetch(`${API_BASE}/api/v1/auth/logout`, { method: 'POST' })
  } catch {
    // best-effort
  }
  useAuthStore.getState().clearAuth()
}

export function handleOIDCCallback(): boolean {
  const params = new URLSearchParams(window.location.search)
  const accessToken = params.get('access_token')
  const refreshToken = params.get('refresh_token')

  if (accessToken && refreshToken) {
    try {
      const payload = JSON.parse(atob(accessToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')))
      const user = {
        id: payload.user_id || payload.sub,
        email: payload.email || '',
        name: payload.name || payload.email || '',
        role: payload.role || 'member',
        org_id: payload.org_id || '',
      }
      useAuthStore.getState().setAuth(accessToken, refreshToken, user)
      window.history.replaceState({}, '', window.location.pathname)
      return true
    } catch {
      return false
    }
  }
  return false
}
